import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShoppingBag } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const TYPES = [
  { key: "breakfast", label: "فطور فلسطيني" },
  { key: "hospitality", label: "ضيافة" },
  { key: "gift", label: "هدية" },
  { key: "ramadan", label: "رمضان" },
  { key: "olive", label: "زيتون" },
  { key: "heritage", label: "تراثية" },
  { key: "economy", label: "اقتصادية" },
  { key: "luxury", label: "فاخرة" },
];

const BUDGETS = [25, 50, 100];

export default function BasketBuilder() {
  const [params] = useSearchParams();
  const isGift = params.get("type") === "gift";
  const [type, setType] = useState(isGift ? "gift" : "breakfast");
  const [budget, setBudget] = useState(null);
  const [basket, setBasket] = useState(null);
  const [loading, setLoading] = useState(false);
  const { add } = useCart();

  const build = async () => {
    setLoading(true);
    try {
      const r = await api.post("/baskets/build", { basket_type: type, budget });
      setBasket(r.data);
    } catch { toast.error("تعذّر بناء السلة"); }
    setLoading(false);
  };

  useEffect(() => { build(); }, [type, budget]);

  const addAll = () => {
    if (!basket) return;
    basket.items.forEach((i) => add({ sku: i.sku, slug: i.slug, name_ar: i.name_ar, price: i.price, weight_grams: i.weight_grams, images: { main: i.image } }, i.qty));
    toast.success("أُضيفت السلة كاملة");
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="relative overflow-hidden bg-olive-deep py-14 text-ivory">
        <div className="tatreez-bg absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-7xl px-6">
          <span className="mb-2 inline-block rounded-full bg-ivory/10 px-3 py-1 text-xs font-semibold text-earth">{isGift ? "هدية من فلسطين" : "ابنِ سلتك"}</span>
          <h1 className="font-display text-3xl font-bold sm:text-4xl">{isGift ? "هدية من فلسطين" : "ابنِ سلتك الفلسطينية"}</h1>
          <p className="mt-3 max-w-xl text-ivory/75">اختر نوع السلة والميزانية، ودع سهول جنين يقترح لك مزيجاً متكاملاً يمكنك تعديله.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-8 lg:grid-cols-[320px_1fr]">
          <aside className="space-y-6">
            <div>
              <h3 className="mb-3 font-display font-semibold">نوع السلة</h3>
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button key={t.key} data-testid={`basket-type-${t.key}`} onClick={() => setType(t.key)}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${type === t.key ? "border-olive bg-olive text-ivory" : "border-olive/20 bg-white text-ink/70 hover:border-olive/50"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-3 font-display font-semibold">الميزانية</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setBudget(null)} className={`rounded-full border px-4 py-2 text-sm ${budget === null ? "border-olive bg-olive text-ivory" : "border-olive/20 bg-white text-ink/70"}`}>الكل</button>
                {BUDGETS.map((b) => (
                  <button key={b} data-testid={`basket-budget-${b}`} onClick={() => setBudget(b)}
                    className={`rounded-full border px-4 py-2 text-sm ${budget === b ? "border-olive bg-olive text-ivory" : "border-olive/20 bg-white text-ink/70"}`}>${b}</button>
                ))}
              </div>
            </div>
          </aside>

          <div className="rounded-3xl border border-olive/10 bg-white p-6">
            {loading ? <div className="py-20 text-center text-ink/50">جارِ بناء السلة...</div> : basket && (
              <>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-ink">سلة {basket.name_ar}</h2>
                    <p className="text-sm text-ink/55">{basket.count} منتجات · {(basket.weight_grams / 1000).toFixed(2)} كغ</p>
                  </div>
                  <button data-testid="add-basket-all" onClick={addAll} className="btn-shine flex items-center gap-2 rounded-full bg-olive px-6 py-3 font-semibold text-ivory transition-colors hover:bg-olive-dark">
                    <ShoppingBag className="h-5 w-5" /> أضف السلة كاملة
                  </button>
                </div>

                <div className="space-y-3" data-testid="basket-items">
                  {basket.items.map((i) => (
                    <div key={i.sku} className="flex items-center gap-4 rounded-2xl border border-olive/10 p-3">
                      <Link to={`/product/${i.slug}`} className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-sand"><img src={i.image} alt={i.name_ar} className="h-full w-full object-cover" /></Link>
                      <Link to={`/product/${i.slug}`} className="flex-1 font-display font-semibold text-ink hover:text-olive">{i.name_ar}</Link>
                      <span className="font-display font-bold text-olive-dark">{fmtPrice(i.price)}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 rounded-2xl bg-olive/5 p-5 sm:grid-cols-4">
                  <Stat label="عدد المنتجات" value={basket.count} />
                  <Stat label="الإجمالي الفرعي" value={fmtPrice(basket.subtotal)} />
                  <Stat label="الشحن" value={fmtPrice(basket.shipping)} />
                  <Stat label="الإجمالي" value={fmtPrice(basket.total)} highlight />
                </div>
                {isGift && (
                  <div className="mt-6 rounded-2xl border border-earth/30 bg-earth/5 p-5">
                    <h4 className="mb-2 flex items-center gap-2 font-display font-semibold text-ink"><Check className="h-4 w-4 text-earth" /> لمسة الهدية</h4>
                    <input placeholder="اسم المستلم" className="mb-3 w-full rounded-xl border border-olive/20 px-4 py-2.5 text-sm outline-none" data-testid="gift-recipient" />
                    <textarea placeholder="اكتب رسالتك الخاصة..." rows={3} className="w-full rounded-xl border border-olive/20 px-4 py-2.5 text-sm outline-none" data-testid="gift-message" />
                    <p className="mt-2 text-xs text-ink/40">التغليف الفاخر يُضاف عند إتمام الطلب.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className="text-center">
    <div className={`font-display text-lg font-bold ${highlight ? "text-olive" : "text-ink"}`}>{value}</div>
    <div className="text-xs text-ink/50">{label}</div>
  </div>
);
